/**
 * pricingMixin.js
 *
 * Drop into any page that needs pricing plans + payment:
 *
 *   import { pricingMixin } from '@/lib/pricingMixin';
 *   export default { mixins: [pricingMixin], ... }
 *
 * Then in your template:
 *   - activePlans, activeCycle, billingCycles, currencySymbol, formatCurrency
 *   - localPaymentChannels, internationalPaymentChannels
 *   - paymentModal(plan) — opens login modal if not logged in, payment modal if logged in
 *   - loginModalOpen, paymentModalOpen, currentData, validationErrors
 *   - login object, loginUser(), parsedDescriptions()
 */

import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { payWithFlutterwave } from '@/services/flutterwave';
import { payWithPaystack }    from '@/services/paystack';
import { useStore }  from 'vuex';
import { useRouter } from 'vue-router';
import { useToast }  from 'vue-toastification';
import hostName from '@/main';

export const pricingMixin = {

  data() {
    return {
      // ── Plans ─────────────────────────────────────────────────────
      monthly_plans:    [],
      quarterly_plans:  [],
      halfyear_plans:   [],
      yearly_plans:     [],
      activeCycle:      'monthly',

      billingCycles: [
        { key: 'monthly',   label: 'Monthly'   },
        { key: 'quarterly', label: 'Quarterly' },
        { key: 'halfyear',  label: 'Half Year' },
        { key: 'yearly',    label: 'Yearly'    },
      ],

      // ── Payment channels ──────────────────────────────────────────
      payment_channels: [],

      // ── Currency ──────────────────────────────────────────────────
      countryCode:    '',
      currencySymbol: '',

      // ── Modals ────────────────────────────────────────────────────
      loginModalOpen:   false,
      paymentModalOpen: false,
      currentData:      null,

      // ── Login form (inside login modal) ───────────────────────────
      login:            { email: '', password: '' },
      validationErrors: [],
      loginLoading:     false,

      // ── Stripe ────────────────────────────────────────────────────
      stripe:      null,
      backend_url: hostName,

      // ── Internal refs ─────────────────────────────────────────────
      _pricingStore:  null,
      _pricingRouter: null,
      _pricingToast:  null,
    };
  },

  computed: {
    activePlans() {
      const map = {
        monthly:   this.monthly_plans,
        quarterly: this.quarterly_plans,
        halfyear:  this.halfyear_plans,
        yearly:    this.yearly_plans,
      };
      return map[this.activeCycle] ?? [];
    },

    localPaymentChannels() {
      return this.payment_channels.filter(c => c.type === 'local');
    },

    internationalPaymentChannels() {
      return this.payment_channels.filter(c => c.type === 'international');
    },
  },

  methods: {

    // ── Init (call once in mounted) ──────────────────────────────────
    async initPricing() {
      // Lazily resolve composables (safe in Options API via this pattern)
      this._pricingStore  = this._pricingStore  ?? useStore();
      this._pricingRouter = this._pricingRouter ?? useRouter();
      this._pricingToast  = this._pricingToast  ?? useToast();

      this._loadCountry();
      this._getPricingPlans();
      this._getPaymentChannels();
      // Guard: only init Stripe if a publishable key is configured.
      // Without this guard, loadStripe() throws when the env var is missing.
      const stripeKey = process.env.VUE_APP_STRIPE_PUBLIC_KEY;
      if (stripeKey) {
        this.stripe = await loadStripe(stripeKey);
      }
    },

    // ── Country / currency ───────────────────────────────────────────
    _loadCountry() {
      this.countryCode    = localStorage.getItem('country_code') || 'GB';
      this.currencySymbol = this.countryCode === 'NG' ? '₦' : '£';
    },

    // ── Formatting ───────────────────────────────────────────────────
    formatCurrency(amount) {
      amount = Number(amount) || 0;
      if (this.countryCode === 'NG') return (amount * 2000).toLocaleString();
      return amount.toLocaleString();
    },

    parsedDescriptions(raw) {
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (_) { return []; }
    },

    // Build a human-readable feature list from the plan's access/limit fields.
    // Used as a fallback when descriptions is null.
    planFeatures(plan) {
      if (!plan) return [];
      const parsed = this.parsedDescriptions(plan.descriptions);
      if (parsed.length) return parsed;

      const feats = [];

      // ── Limits ────────────────────────────────────────────────────────
      if (plan.max_employee)  feats.push(plan.max_employee === 0 ? 'Unlimited employees' : `Up to ${plan.max_employee} employee${plan.max_employee > 1 ? 's' : ''}`);
      if (plan.max_product)   feats.push(plan.max_product  === 0 ? 'Unlimited products'  : `Up to ${Number(plan.max_product).toLocaleString()} products`);
      if (plan.max_location)  feats.push(plan.max_location === 0 ? 'Unlimited locations' : `Up to ${plan.max_location} location${plan.max_location > 1 ? 's' : ''}`);
      if (plan.max_business)  feats.push(plan.max_business === 0 ? 'Unlimited businesses': `Up to ${plan.max_business} business${plan.max_business > 1 ? 'es' : ''}`);
      if (plan.max_sale)      feats.push(plan.max_sale     === 0 ? 'Unlimited sales'     : `Up to ${Number(plan.max_sale).toLocaleString()} sales`);
      if (plan.max_purchase)  feats.push(plan.max_purchase === 0 ? 'Unlimited purchases' : `Up to ${Number(plan.max_purchase).toLocaleString()} purchases`);
      if (plan.max_invoice)   feats.push(plan.max_invoice  === 0 ? 'Unlimited invoices'  : `Up to ${Number(plan.max_invoice).toLocaleString()} invoices`);
      if (plan.max_billing)   feats.push(plan.max_billing  === 0 ? 'Unlimited billings'  : `Up to ${Number(plan.max_billing).toLocaleString()} billings`);
      if (plan.max_expense)   feats.push(plan.max_expense  === 0 ? 'Unlimited expenses'  : `Up to ${Number(plan.max_expense).toLocaleString()} expenses`);

      // ── Module access ─────────────────────────────────────────────────
      if (plan.sales_access)          feats.push('Sales management');
      if (plan.purchase_access)       feats.push('Purchase management');
      if (plan.invoice_access)        feats.push('Invoice management');
      if (plan.billing_access)        feats.push('Billing management');
      if (plan.expense_access)        feats.push('Expense tracking');
      if (plan.hr_access)             feats.push('HR module');
      if (plan.payroll_access)        feats.push('Payroll management');
      if (plan.creditor_access)       feats.push('Creditors tracking');
      if (plan.debitor_access)        feats.push('Debtors tracking');
      if (plan.administrator_access)  feats.push('Administration & roles');
      if (plan.business_access)       feats.push('Multi-business management');
      if (plan.settings_access)       feats.push('Settings');

      return feats;
    },

    // ── Modal control ─────────────────────────────────────────────────
    paymentModal(plan) {
      this.currentData = plan;
      if (localStorage.getItem('token')) {
        this.paymentModalOpen = true;
      } else {
        this.login            = { email: '', password: '' };
        this.validationErrors = [];
        this.loginModalOpen   = true;
      }
    },

    closePricingModals() {
      this.paymentModalOpen = false;
      this.loginModalOpen   = false;
    },

    // ── Login (from within the pricing login modal) ───────────────────
    async loginUser() {
      if (!this.login.email || !this.login.password) {
        this._pricingToast?.warning('Email and password cannot be empty.');
        return;
      }
      this.loginLoading = true;
      try {
        const { data } = await axios.post('/login-web', {
          email:    this.login.email,
          password: this.login.password,
        });
        localStorage.setItem('token', data.data.original.accessToken);
        axios.defaults.headers['Authorization'] = `Bearer ${data.data.original.accessToken}`;
        this._pricingToast?.success('Login successful.');
        this.loginModalOpen = false;

        // Re-fetch payment channels now that the user is authenticated.
        // The API may gate channels behind auth, so the payment modal
        // would appear empty if channels were fetched before login.
        await this._getPaymentChannels();

        // Only open payment modal once channels are ready
        this.paymentModalOpen = true;
      } catch (err) {
        const status  = err.response?.status;
        const message = err.response?.data?.message;
        if (status === 400) {
          this.validationErrors = Array.isArray(message) ? message : [message];
        } else if (status === 402) {
          this._pricingRouter?.push('send-email-verifcation');
          this._pricingToast?.error(message);
        } else {
          this._pricingToast?.error(message ?? 'Login failed. Please try again.');
        }
      } finally {
        this.loginLoading = false;
      }
    },

    // ── Payment handlers ──────────────────────────────────────────────
    makeFlutterwavePayment(data, channel) {
      const store = this._pricingStore;
      const amount = Number(this.formatCurrency(data.amount).toString().replace(/,/g, ''));

      payWithFlutterwave({
        public_key: channel.public_key,
        amount,
        email: store?.state.getLoggedInUser?.email,
        name:  `${store?.state.getLoggedInUser?.first_name} ${store?.state.getLoggedInUser?.last_name}`,
        callback: (response) => {
          axios.post('/payment/verify-flutterwave-payment', {
            transaction_id:     response.transaction_id,
            plan_detail_id:     data.id,
            payment_channel_id: channel.id,
          }).then(res => {
            const result       = res.data.data.data;
            const subscription = res.data.data.subscription;
            if (result.status === 'successful') {
              this._pricingRouter?.push({
                path:  '/payment-success',
                query: {
                  ref:    result.tx_ref,
                  amount: result.amount,
                  plan:   'Subscription Plan - ' + data.name,
                  expiry: subscription.expiry_date,
                },
              });
            }
          }).catch(err => {
            this._pricingToast?.error(err?.response?.data?.message || 'Payment verification failed');
          });
        },
      });
    },

    makePaystackPayment(data, channel) {
      const amount = Number(this.formatCurrency(data.amount).toString().replace(/,/g, ''));
      payWithPaystack({
        channel,
        data,
        user:   this._pricingStore?.state.getLoggedInUser,
        router: this._pricingRouter ?? this.$router,
        toast:  this._pricingToast,
        amount,
      });
    },

    async payWithStripe(data, channel) {
      const currency = this.countryCode === 'NG' ? 'ngn' : 'gbp';
      try {
        const { data: res } = await axios.post('/payment/initiate-stripe', {
          amount:             data.amount,
          currency,
          name:               'Subscription Payment - ' + data.name,
          plan_detail_id:     data.id,
          payment_channel_id: channel.id,
        });
        window.location.href = res.data;
      } catch (err) {
        this._pricingToast?.error(err.response?.data?.message);
      }
    },

    // ── Data fetching ─────────────────────────────────────────────────
    _getPricingPlans() {
      axios.get('/subscription/pricing-plans')
        .then(({ data }) => {
          this.monthly_plans   = data.data.monthly;
          this.quarterly_plans = data.data.quarterly;
          this.halfyear_plans  = data.data.halfyear;
          this.yearly_plans    = data.data.yearly;
        })
        .catch(() => {});
    },

    _getPaymentChannels() {
      // Returns a Promise so it can be awaited (e.g. after login)
      return axios.get('/subscription/payment-channels')
        .then(({ data }) => { this.payment_channels = data.data; })
        .catch(() => {});
    },
  },
};